import { PropsWithChildren, RefObject, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdTripOrigin } from 'react-icons/md';
import {
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Listbox,
  ListboxItem,
  Popover,
  PopoverContent,
  Selection,
} from '@ds';
import { CSShiftOptionType } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/types';
import {
  CS_SHIFT_OPTION_DROPDOWN_LIST,
  organSelectCSShiftOptionTypes,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import {
  getShiftFromShiftOption,
  getShiftOptionFromShift,
  getShiftOptionIconColor,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/utils';
import { SCHEDULE_ORGAN_SELECT_LIST } from '@/constants/case/case.constants';
import { CSShift, CSShiftSelectPayload, CSShiftTimeGroup } from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';
import { CSShiftOrgansSubDropdown, CSTimeGroupToggle } from './components';

type CSShiftStatusCellDropdownProps = PropsWithChildren<{
  title?: string;
  isPlacementBottom?: boolean;
  shiftData?: CSShift;
  onShiftChange: (shiftPayload?: CSShiftSelectPayload) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isExternalHub?: boolean;
  canReset?: boolean;
  allowedOrgans?: OrganType[];
  canPhoneSupport?: boolean;
  resourceTimeGroup?: CSShiftTimeGroup;
  showTimeGroup?: boolean;
}>;

export const CSShiftCellDropdown = ({
  title = 'Change Status',
  isPlacementBottom,
  isOpen,
  onOpenChange,
  triggerRef,
  shiftData,
  onShiftChange,
  isExternalHub,
  canReset,
  allowedOrgans,
  canPhoneSupport,
  resourceTimeGroup,
  showTimeGroup,
}: CSShiftStatusCellDropdownProps) => {
  const [timeGroup, setTimeGroup] = useState<CSShiftTimeGroup>(
    shiftData?.timeGroup ?? resourceTimeGroup ?? CSShiftTimeGroup.GroupA,
  );

  const selectedShiftOption = isExternalHub ? undefined : getShiftOptionFromShift(shiftData);
  const selectedOrganOption = organSelectCSShiftOptionTypes.includes(selectedShiftOption as CSShiftOptionType)
    ? shiftData?.organs
    : undefined;

  // In period mode an option is highlighted as selected only when both the shift type and the
  // time group match. This allows re-selecting the same shift type with a different time group.
  // In legacy mode time group is not involved, so only the shift type is matched.
  const isSameTimeGroup = !showTimeGroup || shiftData?.timeGroup === timeGroup;
  const selectedShiftKey = selectedShiftOption && isSameTimeGroup ? selectedShiftOption : undefined;

  const withTimeGroup = (payload?: Omit<CSShiftSelectPayload, 'timeGroup'>): CSShiftSelectPayload | undefined => {
    if (!payload) return payload;
    return showTimeGroup ? { ...payload, timeGroup } : payload;
  };

  const onSelectionChange = (keys: Selection) => {
    if (keys !== 'all') {
      const selected = Array.from(keys)[0] as CSShiftOptionType | undefined;
      onShiftChange(withTimeGroup(getShiftFromShiftOption(selected)));
      onOpenChange(false);
    }
  };

  const organsSelectionHandler = (optionType: CSShiftOptionType) => (keys: Selection) => {
    if (keys !== 'all') {
      const organs = Array.from(keys) as OrganType[];
      onShiftChange(organs.length > 0 ? withTimeGroup(getShiftFromShiftOption(optionType, organs)) : undefined);
      onOpenChange(false);
    }
  };

  return createPortal(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <Popover
      placement={isPlacementBottom ? 'bottom-start' : 'right-start'}
      // showArrow
      // @ts-expect-error HeroUI Popover triggerRef expects RefObject<HTMLElement>, React 19 provides RefObject<HTMLButtonElement | null>
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      shouldCloseOnInteractOutside={(el) => el !== triggerRef.current && !triggerRef.current?.contains(el)}
    >
      <PopoverContent>
        <Listbox
          selectionMode="single"
          disallowEmptySelection={!canReset}
          selectedKeys={selectedShiftKey ? [selectedShiftKey] : []}
          disabledKeys={canPhoneSupport ? undefined : [CSShiftOptionType.PhoneSupport]}
          onSelectionChange={onSelectionChange}
          aria-label="Change Shift Status"
          topContent={
            <>
              <div className="flex items-center justify-between gap-4">
                <span className="text-s-m text-base-20">{title}</span>
                {showTimeGroup && <CSTimeGroupToggle value={timeGroup} onChange={setTimeGroup} />}
              </div>
              <Divider orientation="horizontal" />
            </>
          }
        >
          {CS_SHIFT_OPTION_DROPDOWN_LIST.map((optionItem) => {
            const color = getShiftOptionIconColor(optionItem.value);
            const isPhoneSupport = optionItem.value === CSShiftOptionType.PhoneSupport;
            const isMultiOrgan = !isPhoneSupport && organSelectCSShiftOptionTypes.includes(optionItem.value);
            const isPlainText = !isPhoneSupport && !isMultiOrgan;
            const onOrgansSelectionChange = organsSelectionHandler(optionItem.value);

            return (
              <ListboxItem
                isReadOnly={isPhoneSupport}
                key={optionItem.value}
                textValue={optionItem.name}
                startContent={<MdTripOrigin size={16} className={color} />}
              >
                {isPhoneSupport && (
                  <Dropdown placement="right" closeOnSelect={false}>
                    <DropdownTrigger>
                      <div>{optionItem.name}</div>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Select Organs"
                      // TMDXLP-14091: Kidneys hidden from Clinical Lead organ picker. Revert to ORGAN_SELECT_LIST to restore.
                      items={SCHEDULE_ORGAN_SELECT_LIST}
                      disallowEmptySelection={!canReset}
                      selectionMode="single"
                      selectedKeys={selectedOrganOption ?? []}
                      onSelectionChange={onOrgansSelectionChange}
                    >
                      {(organItem) => <DropdownItem key={organItem.value}>{organItem.name}</DropdownItem>}
                    </DropdownMenu>
                  </Dropdown>
                )}
                {isMultiOrgan && (
                  <CSShiftOrgansSubDropdown
                    title={optionItem.name}
                    allowedOrgans={allowedOrgans}
                    initialOrgans={optionItem.value === selectedShiftOption ? selectedOrganOption : undefined}
                    onApply={onOrgansSelectionChange}
                  />
                )}
                {isPlainText && optionItem.name}
              </ListboxItem>
            );
          })}
        </Listbox>
      </PopoverContent>
    </Popover>,
    document.body,
  );
};
